import { CheckCircle, BarChart2, Sparkles, Upload, Users, Star, Building2, Check } from "lucide-react";
import { formatHoursMinutes } from "@/lib/video-metadata";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { useCurrency, getPlanPrices } from "@/hooks/use-currency";
import { WalletPlanDiscountNote } from "@/components/billing/WalletPlanDiscountNote";
import { PlanRenewalDate } from "@/components/billing/PlanRenewalDate";
import type { PlanDatesSubscriptionLike } from "@/lib/billing/resolvePlanDates";
import { createWhatsAppUrl } from "@/constants/contact";
import { useStoreCheckoutGuard } from "@/hooks/use-store-checkout-guard";
import { useNavigate, useSearchParams } from "react-router-dom";

interface PricingPlansClippersProps {
  plans: any[];
  loading: boolean;
  error?: any;
  currentPlan?: any;
  subscription?: PlanDatesSubscriptionLike | null;
  subscriptionId?: string;
}

interface DescriptionItem {
  for?: string;
  label?: string;
  label_en?: string;
  tooltip?: string;
  coming_soon?: boolean;
  featured?: boolean;
  exclusive?: boolean;
  description?: string;
  description_en?: string;
  link?: string;
  link_label?: string;
  link_label_en?: string;
  icon?: "library" | "automatic";
  /** Explainer modal key; plan data may name one this build doesn't know. */
  modal?: string;
}

type ModalType = "community" | "competition" | "library";

interface ModalConfig {
  icon: React.ElementType;
  titlePt: string;
  titleEn: string;
  subtitlePt: React.ReactNode;
  subtitleEn: React.ReactNode;
  featuresPt: string[];
  featuresEn: string[];
  highlightPt: string;
  highlightEn: string;
}

const MODAL_CONFIGS: Record<ModalType, ModalConfig> = {
  community: {
    icon: Users,
    titlePt: "Comunidade The Clippers no WhatsApp",
    titleEn: "The Clippers WhatsApp Community",
    subtitlePt: "Grupo exclusivo no WhatsApp pra quem assina qualquer plano.",
    subtitleEn: "Exclusive WhatsApp group for all plan subscribers.",
    featuresPt: [
      "Troque ideia com outros clipadores",
      "Dicas e estratégias de cortes que viralizam",
      "Suporte direto da equipe AutoClipper",
      "Oportunidades e parcerias exclusivas",
    ],
    featuresEn: [
      "Connect and share ideas with other clippers",
      "Tips and strategies for viral cuts",
      "Direct support from the AutoClipper team",
      "Exclusive opportunities and partnerships",
    ],
    highlightPt: "A maior comunidade de clipadores do Brasil.",
    highlightEn: "The largest clippers community in Brazil.",
  },
  competition: {
    icon: Star,
    titlePt: "Competições de Corte",
    titleEn: "Clipping Competitions",
    subtitlePt: "Competições onde clipadores disputam pra fazer os melhores cortes de influenciadores.",
    subtitleEn: "Competitions where clippers compete to make the best cuts from influencers.",
    featuresPt: [
      "Competições com influenciadores parceiros",
      "Assinantes têm preferência na aprovação",
      "Ganhe visibilidade e prêmios",
      "Quanto maior o plano, maior a prioridade",
    ],
    featuresEn: [
      "Competitions with partner influencers",
      "Subscribers get priority approval",
      "Gain visibility and prizes",
      "Higher plan = higher priority",
    ],
    highlightPt: "Pro+ tem prioridade máxima nas competições.",
    highlightEn: "Pro+ has maximum priority in competitions.",
  },
  library: {
    icon: Users,
    titlePt: "Acervo da Comunidade The Clippers",
    titleEn: "The Clippers Community Library",
    subtitlePt: (
      <>
        O acervo compartilha só o <strong>vídeo já processado</strong>, pronto pra você clipar.{" "}
        Nenhum corte seu é compartilhado.
      </>
    ),
    subtitleEn: (
      <>
        The library shares only the <strong>already processed video</strong>, ready for you to clip.{" "}
        None of your cuts are shared.
      </>
    ),
    featuresPt: [
      "Só o vídeo processado é compartilhado, nunca seus cortes",
      "As recomendações da IA são únicas pra cada clipador",
      "Seus cortes são 100% privados",
      "Sem precisar fazer upload de tudo, aproveite o que já foi processado",
      "Escale sua produção sem dor de cabeça",
    ],
    featuresEn: [
      "Only the processed video is shared, never your cuts",
      "AI recommendations are unique to each clipper",
      "Your cuts are 100% private",
      "No need to upload everything — use what's already processed",
      "Scale your production effortlessly",
    ],
    highlightPt: "O processamento é coletivo, mas os cortes são só seus.",
    highlightEn: "The processing is collective, but the cuts are only yours.",
  },
};

/** Plan data can name a modal this build has no copy for; its link is hidden. */
const isKnownModal = (modal?: string): modal is ModalType =>
  !!modal && Object.prototype.hasOwnProperty.call(MODAL_CONFIGS, modal);

const FeatureModal = ({
  modalType,
  isBRL,
  onClose,
}: {
  modalType: ModalType | null;
  isBRL: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  if (!modalType) return null;
  const config = MODAL_CONFIGS[modalType];
  const Icon = config.icon;
  const title = isBRL ? config.titlePt : config.titleEn;
  const subtitle = isBRL ? config.subtitlePt : config.subtitleEn;
  const features = isBRL ? config.featuresPt : config.featuresEn;
  const highlight = isBRL ? config.highlightPt : config.highlightEn;

  return (
    <Dialog open={!!modalType} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Icon className="w-5 h-5 text-primary flex-shrink-0" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-2">{subtitle}</p>

        <ul className="space-y-2">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              {f}
            </li>
          ))}
        </ul>

        <div className="rounded-lg bg-muted/60 px-4 py-3 text-sm text-center text-muted-foreground">
          {highlight}
        </div>

        <DialogFooter className="gap-2">
          <Button className="flex-1" onClick={onClose}>
            {t('pricing.clippers.explorePlans')}
          </Button>
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {t('pricing.clippers.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FeaturedItem = ({
  item,
  isBRL,
  onModalOpen,
}: {
  item: DescriptionItem;
  isBRL: boolean;
  onModalOpen: (modal: ModalType) => void;
}) => {
  const { t } = useTranslation();
  const label = isBRL ? item.label : (item.label_en || item.label);
  const description = isBRL ? item.description : (item.description_en || item.description);
  const linkLabel = isBRL ? item.link_label : (item.link_label_en || item.link_label);
  const IconComponent = item.icon === "automatic" ? Sparkles : BarChart2;

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
      <div className="flex items-start gap-2">
        <IconComponent className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{label}</span>
            {item.exclusive && (
              <Badge className="text-[10px] px-1.5 py-0 bg-primary/20 text-primary border-primary/30 font-semibold">
                {t('pricing.clippers.exclusive')}
              </Badge>
            )}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
          {isKnownModal(item.modal) && (
            <button
              onClick={() => isKnownModal(item.modal) && onModalOpen(item.modal)}
              className="text-xs text-primary hover:underline mt-1 inline-block"
            >
              {linkLabel || t('common.learnMore')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PricingPlansClippers = ({
  plans,
  loading,
  error,
  currentPlan,
  subscription,
}: PricingPlansClippersProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blockStoreCheckout = useStoreCheckoutGuard();
  const { formatCurrency, isBRL } = useCurrency();
  const [isYearly, setIsYearly] = useState(() => searchParams.get('yearly') === 'true');
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);

  const isCurrentPlan = (plan: any) => {
    if (!currentPlan) return false;
    return (
      plan.slug === currentPlan.slug ||
      plan.documentId === currentPlan.documentId ||
      plan.name === currentPlan.name
    );
  };

  const handleCheckout = (plan: any) => {
    if (!plan.documentId) return;
    if (blockStoreCheckout(subscription)) return;
    const couponParam = searchParams.get('coupon');
    const params = new URLSearchParams();
    params.set('yearly', String(isYearly));
    if (couponParam) params.set('coupon', couponParam);
    navigate(`/plans/${plan.documentId}?${params.toString()}`);
  };

  const getDescriptionItems = (plan: any): DescriptionItem[] => {
    const desc = Array.isArray(plan?.description_items) ? plan.description_items : [];
    return desc.filter(
      (d: any) => d && typeof d === "object" && (!d.for || d.for === 'other_channels')
    );
  };

  const getFeaturedItems = (plan: any): DescriptionItem[] =>
    getDescriptionItems(plan).filter((d) => d.featured);

  const getRegularItems = (plan: any): DescriptionItem[] =>
    getDescriptionItems(plan).filter((d) => !d.featured);

  const getProcessingTimeFormatted = (plan: any): string => {
    const secs = Number(plan?.processing_seconds ?? 0);
    return formatHoursMinutes(secs);
  };

  return (
    <div className="flex-1 p-4 lg:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t('pricing.clippers.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('pricing.clippers.subtitle1')}
          </p>
          <p className="text-muted-foreground font-medium">
            {t('pricing.clippers.subtitle2')}
          </p>
          {error && (
            <p className="text-sm text-destructive mt-2">{t('pricing.failedToLoadPlans')}</p>
          )}
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className={`text-sm ${!isYearly ? 'font-semibold' : 'text-muted-foreground'}`}>
            {t('pricing.monthly')}
          </span>
          <Switch checked={isYearly} onCheckedChange={setIsYearly} />
          <span className={`text-sm ${isYearly ? 'font-semibold' : 'text-muted-foreground'}`}>
            {t('pricing.yearly')}
          </span>
        </div>

        {loading ? (
          <div className="text-center text-sm text-muted-foreground">{t('pricing.loadingPlans')}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan: any, index: number) => {
              const isPopular = (plan?.order ?? 0) === 2 || (plan?.slug ?? '') === 'prata';
              const isCurrent = isCurrentPlan(plan);
              const { value: priceValue, valueYearly: yearlyTotal } = getPlanPrices(plan, isBRL);
              const yearlyMonthlyEquivalent = yearlyTotal > 0 ? yearlyTotal / 12 : 0;
              const displayPrice = isYearly ? yearlyMonthlyEquivalent : priceValue;
              const maxExports = Number(plan?.max_library_exports ?? 0);
              const pricePerCut = maxExports > 0 ? Math.floor((displayPrice / maxExports) * 100) / 100 : 0;
              const processingTimeFormatted = getProcessingTimeFormatted(plan);
              const featuredItems = getFeaturedItems(plan);
              const regularItems = getRegularItems(plan);

              return (
                <Card
                  key={plan?.slug ?? index}
                  className={`relative overflow-hidden transition-all duration-300 ${
                    isCurrent
                      ? 'border-primary bg-primary/5 opacity-80'
                      : isPopular
                      ? 'border-primary/60 shadow-glow bg-gradient-to-br from-primary/5 to-primary/10'
                      : 'border-border'
                  }`}
                >
                  {/* Banner */}
                  {isCurrent && (
                    <div className="bg-primary/10 text-primary font-bold text-center py-2 text-sm">
                      {t('pricing.currentPlan')}
                    </div>
                  )}
                  {!isCurrent && isPopular && (
                    <div className="bg-gradient-primary text-black font-bold text-center py-2 text-sm">
                      {t('pricing.clippers.mostPopular')}
                    </div>
                  )}

                  <CardHeader className={`text-center ${isCurrent || isPopular ? 'pt-4' : 'pt-6'}`}>
                    <CardTitle className="text-2xl font-bold">{plan?.name}</CardTitle>
                    {displayPrice > 0 ? (
                      <div>
                        <p className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                          {formatCurrency(displayPrice)}
                          <span className="text-base text-muted-foreground font-normal">/{t('pricing.clippers.perMonthShort')}</span>
                        </p>
                        {isYearly && yearlyTotal > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('common.billedAnnually')} · {formatCurrency(yearlyTotal)}
                          </p>
                        )}
                        <WalletPlanDiscountNote
                          plan={plan}
                          priceReais={displayPrice}
                          formatCurrency={formatCurrency}
                        />
                      </div>
                    ) : (
                      <p className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                        {t('common.free')}
                      </p>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Metrics grid */}
                    {maxExports > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-md bg-muted/60 p-3 text-center">
                          <p className="text-2xl font-bold">{maxExports}</p>
                          <p className="text-xs text-muted-foreground">{t('pricing.clippers.cutsPerMonth')}</p>
                        </div>
                        <div className="rounded-md bg-muted/60 p-3 text-center">
                          <p className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                            {formatCurrency(pricePerCut)}
                          </p>
                          <p className="text-xs text-muted-foreground">{t('pricing.clippers.perCut')}</p>
                        </div>
                      </div>
                    )}

                    {/* CTA Button */}
                    {priceValue > 0 && (
                      <Button
                        variant={isCurrent ? 'secondary' : isPopular ? 'default' : 'outline'}
                        className="w-full"
                        size="lg"
                        disabled={isCurrent}
                        onClick={() => !isCurrent && handleCheckout(plan)}
                      >
                        {isCurrent
                          ? t('pricing.currentPlan')
                          : t('pricing.clippers.subscribeNow')}
                      </Button>
                    )}
                    {isCurrent && (
                      <PlanRenewalDate subscription={subscription} className="items-center" />
                    )}

                    <div className="space-y-2">
                      {/* Processing hours */}
                      {processingTimeFormatted !== '0h' && (
                        <div className="flex items-center gap-2">
                          <Upload className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-sm">
                            {t('pricing.clippers.timeForNewVideos', { time: processingTimeFormatted })}
                          </span>
                        </div>
                      )}

                      {/* Featured items (boxed) */}
                      {featuredItems.map((item, idx) => (
                        <FeaturedItem
                          key={idx}
                          item={item}
                          isBRL={isBRL}
                          onModalOpen={setActiveModal}
                        />
                      ))}

                      {/* Regular feature items */}
                      {regularItems.map((item, idx) => {
                        const label = isBRL ? item.label : (item.label_en || item.label);
                        const linkLabel = isBRL ? item.link_label : (item.link_label_en || item.link_label);
                        return (
                          <div key={idx} className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span className={`text-sm ${item.coming_soon ? 'text-muted-foreground' : ''}`}>
                                {label}
                                {item.coming_soon ? ` (${t('common.comingSoon')})` : ''}
                              </span>
                            </div>
                            {isKnownModal(item.modal) && (
                              <button
                                onClick={() => isKnownModal(item.modal) && setActiveModal(item.modal)}
                                className="text-xs text-primary hover:underline flex-shrink-0"
                              >
                                {linkLabel || t('common.learnMore')}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Enterprise Section */}
        <Card className="max-w-4xl mx-auto mt-16 bg-gradient-to-br from-secondary/50 to-card border-primary/20">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-black" />
                  </div>
                  <h2 className="text-3xl font-bold">{t('common.enterprise')}</h2>
                </div>
                <p className="text-lg text-muted-foreground mb-6">
                  {t('pricing.plans.enterprise.description')}
                </p>
                <ul className="space-y-3 mb-6">
                  {(t('pricing.plans.enterprise.features', { returnObjects: true }) as string[]).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center md:text-right">
                <div className="mb-6">
                  <div className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-2">
                    {t('pricing.plans.enterprise.price')}
                  </div>
                  <p className="text-muted-foreground">{t('pricing.plans.enterprise.priceSubtitle')}</p>
                </div>
                <Button size="lg" className="bg-gradient-primary hover:opacity-90" asChild>
                  <a
                    href={createWhatsAppUrl("Hello! I'd like to know more about the AutoClipper Enterprise plan.")}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('common.talkToSpecialist')}
                  </a>
                </Button>
                <p className="text-xs text-muted-foreground mt-4">
                  {t('common.responseIn24h')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-12">
          <p className="text-muted-foreground">
            {t('pricing.haveQuestions')}{" "}
            <a
              href={createWhatsAppUrl(t('plansExtra.whatsappMessage'))}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline font-medium"
            >
              {t('pricing.contactSupport')}
            </a>{" "}
            {t('pricing.customPlanMessage')}
          </p>
        </div>
      </div>

      <FeatureModal
        modalType={activeModal}
        isBRL={isBRL}
        onClose={() => setActiveModal(null)}
      />
    </div>
  );
};

export default PricingPlansClippers;
